// Bluetooth yazıcı röle sistemi:
// - BT yazıcıya bağlı cihaz: kendini "host" olarak heartbeat gönderir + iş kuyruğunu poll eder.
// - BT'siz cihaz: yazdırma denerken hata alırsa veya BT yapılandırması yoksa,
//   raw bytes'ı backend'e gönderir; backend mağazadaki bir host'a atar; o cihaz BT'den basar.

import { API_URL } from '../config/api';
import {
  getBluetoothPrinterInfo,
  printRawToBluetooth,
} from './bluetoothPrinter';

const DEVICE_ID_KEY = 'akikDeviceId';

// Cihaz başına benzersiz ID (localStorage'da saklanır)
export const getDeviceId = () => {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
};

// Tarayıcı/platform okunaklı isim
const getDeviceName = () => {
  const ua = navigator.userAgent || '';
  if (/iPad/.test(ua)) return 'iPad';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Tarayıcı';
};

// base64 encode bytes (Uint8Array)
const bytesToB64 = (bytes) => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
};

const b64ToBytes = (b64) => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

// HOST tarafı: heartbeat
let heartbeatTimer = null;
export const startBTHostHeartbeat = (storeId) => {
  stopBTHostHeartbeat();
  if (!storeId) return;
  const send = async () => {
    try {
      const info = getBluetoothPrinterInfo();
      if (!info.enabled || !info.name) return;
      await fetch(`${API_URL}/bt-printer-devices/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          device_id: getDeviceId(),
          store_id: storeId,
          device_name: getDeviceName(),
          printer_name: info.name,
        }),
      });
    } catch (e) { /* sessizce yut */ }
  };
  send();
  heartbeatTimer = setInterval(send, 15000);
};

export const stopBTHostHeartbeat = () => {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  // Backend'e "ben artık aktif değilim" sinyali (best-effort)
  try {
    fetch(`${API_URL}/bt-printer-devices/heartbeat?device_id=${encodeURIComponent(getDeviceId())}`, {
      method: 'DELETE',
      keepalive: true,
    });
  } catch (e) { /* yut */ }
};

// HOST tarafı: pending job polling. Aldığı işleri BT'den basar.
let pollTimer = null;
export const startBTHostJobPolling = () => {
  stopBTHostJobPolling();
  const tick = async () => {
    try {
      const info = getBluetoothPrinterInfo();
      if (!info.enabled || !info.name) return;
      const res = await fetch(`${API_URL}/print-jobs/pending?device_id=${encodeURIComponent(getDeviceId())}`);
      if (!res.ok) return;
      const data = await res.json();
      if (!data.success || !Array.isArray(data.jobs) || data.jobs.length === 0) return;
      for (const job of data.jobs) {
        try {
          const bytes = b64ToBytes(job.payload_b64);
          await printRawToBluetooth(bytes);
          await fetch(`${API_URL}/print-jobs/${job.id}/complete`, { method: 'POST' });
        } catch (err) {
          await fetch(`${API_URL}/print-jobs/${job.id}/fail`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ error_message: err?.message || 'BT yazdırma hatası' }),
          });
        }
      }
    } catch (e) { /* yut */ }
  };
  pollTimer = setInterval(tick, 4000);
  tick();
};

export const stopBTHostJobPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
};

// KAYNAK tarafı: BT yazdırma — önce yerel BT, başarısız/yapılandırılmamışsa röle ile uzak cihaza
// bytes: Uint8Array
// storeId: hangi mağazanın BT host'u kullanılacak
// returns: { mode: 'local'|'relay', targetName?: string }
export const printBTWithFallback = async (bytes, storeId) => {
  // 1) Önce yerel BT
  const info = getBluetoothPrinterInfo();
  if (info.enabled && info.name) {
    try {
      await printRawToBluetooth(bytes);
      return { mode: 'local' };
    } catch (e) {
      console.warn('Yerel BT yazdırma başarısız, röleye düşülüyor:', e?.message);
      // Yerel BT açıksa ama hata verdiyse de röleye düş
    }
  }

  // 2) Röle: backend'e job at
  if (!storeId) {
    throw new Error('Mağaza bilgisi yok, BT yazdırma yapılamadı');
  }

  const payload_b64 = bytesToB64(bytes);
  const res = await fetch(`${API_URL}/print-jobs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      store_id: storeId,
      source_device_id: getDeviceId(),
      payload_b64,
    }),
  });
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data?.message || 'Mağazada Bluetooth yazıcıya bağlı çevrimiçi cihaz bulunamadı');
  }

  // Job durumunu kısa süreli poll et (en fazla ~30sn)
  const jobId = data.job_id;
  const targetName = data.target_device_name || 'cihaz';
  const start = Date.now();
  while (Date.now() - start < 30000) {
    await new Promise(r => setTimeout(r, 2000));
    try {
      const sres = await fetch(`${API_URL}/print-jobs/${jobId}/status`);
      const sd = await sres.json();
      if (sd?.success && sd.job) {
        if (sd.job.status === 'completed') return { mode: 'relay', targetName };
        if (sd.job.status === 'failed') throw new Error(sd.job.error_message || 'Uzak cihaz yazdıramadı');
      }
    } catch (e) {
      if (e?.message && e.message !== 'Failed to fetch') throw e;
    }
  }
  // Timeout: arka planda devam edebilir, bilgilendirme ile dön
  return { mode: 'relay', targetName, pending: true };
};
